import { StatusCode } from "grpc-web";
import { TournamentsPromiseClient } from "../generated/tournament_grpc_web_pb";
import {
    AdvanceRequest,
    CreateRequest,
    EntrantInput,
    EntrantRequest,
    GetRequest,
    JoinRequest,
    ListRequest,
    MatchStatus as ProtoMatchStatus,
    ReportResultRequest,
    ShareTokenRequest,
    Visibility,
} from "../generated/tournament_pb";
import type { Match, MatchResult, OrganizerWizardValue, Tournament, TournamentSettings } from "@dowty/tournament-ui";
import { GRPC_CLIENT_OPTIONS } from "../grpcConfig";
import TOURNAMENT_BACKEND_URL from "./config";
import type { AuthUser } from "../context/AuthContext";

const client = new TournamentsPromiseClient(TOURNAMENT_BACKEND_URL, null, GRPC_CLIENT_OPTIONS);
export type TournamentVisibility = "public" | "friends" | "password" | "private";

export type TournamentSummary = {
    id: number;
    name: string;
    discipline: string;
    status: Tournament["status"];
};

class TournamentApiError extends Error {
    constructor(message: string, readonly code: StatusCode) {
        super(message);
        this.name = "TournamentApiError";
    }
}

const visibilityNames: TournamentVisibility[] = ["public", "friends", "password", "private"];
const statusNames: Tournament["status"][] = ["Draft", "Running", "Completed", "Cancelled"];
const matchStatusNames: Match["status"][] = ["Pending", "Ready", "Live", "Done", "Bye"];
const matchSides: Match["side"][] = ["W", "L", "GF"];

const parseSettings = (json: string): TournamentSettings => {
    try {
        return JSON.parse(json) as TournamentSettings;
    } catch {
        return { scoring: "winner_only", allowDraws: false, resultEntry: "organizers", tieBreakOrder: [], seedMode: "random", tables: null };
    }
};

const mapTournament = (view: Awaited<ReturnType<typeof client.get>>): Tournament => {
    const settings = parseSettings(view.getSettingsJson());
    return {
        id: view.getId(),
        name: view.getName(),
        discipline: view.getDiscipline(),
        app: view.getApp(),
        contextId: view.getContextId() || null,
        settings: { ...settings, shareLink: view.getShareToken() || null },
        status: statusNames[view.getStatus()] ?? "Draft",
        visibility: visibilityNames[view.getVisibility()] ?? "private",
        version: view.getVersion(),
        stages: view.getStagesList().map((stage) => ({
            id: stage.getOrdinal(), order: stage.getOrdinal(), name: `Stage ${stage.getOrdinal()}`,
            format: stage.getFormat() as Tournament["stages"][number]["format"],
            status: (["Pending", "Running", "Completed"] as const)[stage.getStatus()] ?? "Pending",
        })),
        entrants: view.getEntrantsList().map((entrant) => ({
            id: entrant.getId(), name: entrant.getName(), seed: entrant.getSeed() || null,
            groupNo: null, withdrawn: entrant.getWithdrawn(), members: [],
        })),
        matches: view.getMatchesList().map((match) => ({
            id: match.getId(), stageId: match.getStageOrdinal(), groupNo: null, round: match.getRound(),
            position: match.getPosition(), side: matchSides[match.getSide()] ?? "W",
            entrantAId: match.getEntrantA() || null, entrantBId: match.getEntrantB() || null,
            scoreA: match.getHasScore() ? match.getScoreA() : null,
            scoreB: match.getHasScore() ? match.getScoreB() : null,
            winnerId: match.getWinnerId() || null,
            status: matchStatusNames[match.getStatus()] ?? "Pending",
            walkover: match.getStatus() === ProtoMatchStatus.BYE,
        })),
        standings: view.getStandingsList().map((row) => ({
            stageId: row.getStageOrdinal(), groupNo: null, entrantId: row.getEntrantId(), rank: row.getRank(),
            played: row.getPlayed(), wins: row.getWins(), draws: row.getDraws(), losses: row.getLosses(),
            points: row.getPoints(), scoreFor: row.getScoresFor(),
            scoreAgainst: row.getScoresFor() - row.getScoreDifference(), tieBreaks: [],
        })),
        me: { canOrganize: view.getMe()?.getCanOrganize() ?? false, canReportMatchIds: view.getMe()?.getReportMatchIdsList() ?? [], entrantId: null },
    };
};

const withApiError = async <T>(request: Promise<T>): Promise<T> => {
    try {
        return await request;
    } catch (error) {
        const rpc = error as { message?: string; code?: StatusCode };
        throw new TournamentApiError(rpc.message ?? "Tournament request failed", rpc.code ?? StatusCode.UNKNOWN);
    }
};

export const liveTournamentUrl = (id: number): string =>
    new URL(`/live/tournaments/${encodeURIComponent(id)}`, TOURNAMENT_BACKEND_URL).toString();

export const getTournament = async (id: number, user: AuthUser, password = "", shareToken = ""): Promise<Tournament> => {
    const request = new GetRequest();
    request.setId(id);
    request.setToken(user.token);
    request.setPassword(password);
    request.setShareToken(shareToken);
    return mapTournament(await withApiError(client.get(request)));
};

export const listTournaments = async (contextId: number, user?: AuthUser | null): Promise<TournamentSummary[]> => {
    const request = new ListRequest();
    request.setApp("saufdings");
    request.setContextId(contextId);
    if (user) request.setToken(user.token);
    const response = await withApiError(client.list(request));
    return response.getTournamentsList().map((item) => ({
        id: item.getId(), name: item.getName(), discipline: item.getDiscipline(),
        status: statusNames[item.getStatus()] ?? "Draft",
    }));
};

export const createTournament = async (
    value: OrganizerWizardValue,
    contextId: number,
    visibility: TournamentVisibility,
    user: AuthUser,
    password = "",
): Promise<Tournament> => {
    const request = new CreateRequest();
    request.setToken(user.token);
    request.setName(value.name.trim());
    request.setDiscipline(value.discipline.trim());
    request.setApp("saufdings");
    request.setContextId(contextId);
    request.setFormat(value.format);
    request.setSettingsJson(JSON.stringify({ ...value.settings, shareLink: null, groupCount: value.groupCount, advanceCount: value.advanceCount }));
    request.setVisibility(({ public: Visibility.PUBLIC, friends: Visibility.FRIENDS, password: Visibility.PASSWORD, private: Visibility.PRIVATE })[visibility]);
    request.setPassword(password);
    value.entrants.forEach((entrant) => {
        const item = new EntrantInput();
        item.setName(entrant.name.trim());
        item.setSeed(value.settings.seedMode === "manual" ? entrant.seed ?? 0 : 0);
        request.addEntrants(item);
    });
    return mapTournament(await withApiError(client.create(request)));
};

const resultRequest = (tournament: Tournament, match: Match, result: MatchResult, user: AuthUser): ReportResultRequest => {
    const request = new ReportResultRequest();
    request.setToken(user.token);
    request.setId(tournament.id);
    request.setMatchId(match.id);
    request.setExpectedVersion(tournament.version);
    request.setWinnerId(result.winnerId ?? 0);
    if (result.scoreA !== null && result.scoreB !== null) {
        request.setHasScore(true);
        request.setScoreA(result.scoreA);
        request.setScoreB(result.scoreB);
    }
    return request;
};

export const reportResult = async (tournament: Tournament, match: Match, result: MatchResult, user: AuthUser, correct = false): Promise<Tournament> => {
    const request = resultRequest(tournament, match, result, user);
    const response = await withApiError(correct ? client.correctResult(request) : client.reportResult(request));
    return mapTournament(response);
};

export const changeEntrantStatus = async (tournament: Tournament, entrantId: number, user: AuthUser, reinstate = false): Promise<Tournament> => {
    const request = new EntrantRequest();
    request.setToken(user.token);
    request.setId(tournament.id);
    request.setEntrantId(entrantId);
    request.setExpectedVersion(tournament.version);
    return mapTournament(await withApiError(reinstate ? client.reinstate(request) : client.withdraw(request)));
};

export const advanceTournament = async (tournament: Tournament, user: AuthUser): Promise<Tournament> => {
    const request = new AdvanceRequest();
    request.setToken(user.token);
    request.setId(tournament.id);
    request.setExpectedVersion(tournament.version);
    return mapTournament(await withApiError(client.advance(request)));
};

export const setShareToken = async (tournament: Tournament, enabled: boolean, user: AuthUser): Promise<Tournament> => {
    const request = new ShareTokenRequest();
    request.setToken(user.token);
    request.setId(tournament.id);
    request.setExpectedVersion(tournament.version);
    request.setEnabled(enabled);
    return mapTournament(await withApiError(client.setShareToken(request)));
};

export const joinTournament = async (id: number, user: AuthUser, password = ""): Promise<Tournament> => {
    const request = new JoinRequest();
    request.setToken(user.token);
    request.setId(id);
    request.setPassword(password);
    return mapTournament(await withApiError(client.join(request)));
};
